import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { getSavedFilters, SAVED_FILTERS_PAGES } from '../../utils/savedFilters';
import { getReportPresets, PRESET_PAGES, hrefForPreset } from '../../utils/reportPresets';
import { encodeReportShare } from '../../utils/reportShare';
import { ADSENSE_ENABLED } from '../../utils/productWorkspace';

export default function CommandPalette() {
  const { user } = useAuth();
  const { canPage } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
        setQ('');
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 20);
  }, [open]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const items = useMemo(() => {
    const list = [];
    if (canPage('dashboard')) {
      list.push({ id: 'dash', label: 'Dashboard', hint: 'Overview', to: '/dashboard' });
      list.push({
        id: 'last7',
        label: 'Last 7 days',
        hint: 'Dashboard · apply',
        to: `/dashboard?${encodeReportShare({ preset: 'last7' })}`,
      });
    }
    if (canPage('reporting')) {
      list.push({ id: 'rep', label: 'Reporting', hint: 'Build a report', to: '/reporting' });
    }
    if (canPage('roi')) {
      list.push({ id: 'roi', label: 'ROI', hint: 'Spend vs earn', to: '/roi' });
    }
    if (canPage('my-ads')) {
      list.push({ id: 'my-ads', label: 'Google Ads', hint: 'Connect Ads accounts', to: '/my-ads' });
    }
    if (canPage('admob-dashboard')) {
      list.push({ id: 'admob', label: 'AdMob Dashboard', hint: 'Apps & mediation', to: '/admob/dashboard' });
    }
    if (ADSENSE_ENABLED && canPage('adsense-dashboard')) {
      list.push({ id: 'adsense', label: 'AdSense Dashboard', hint: 'Sites & content', to: '/adsense/dashboard' });
    }
    if (canPage('presets')) {
      list.push({ id: 'presets', label: 'Presets', hint: 'Saved presets', to: '/presets' });
    }
    list.push({ id: 'help', label: 'Help', hint: 'How to use AdNexus', to: '/help' });
    if (canPage('dashboard')) {
      getReportPresets(PRESET_PAGES.dashboard, user?.id).slice(0, 4).forEach((f) => {
        list.push({
          id: `rp-d-${f.id}`,
          label: f.name,
          hint: 'Preset · Dashboard',
          to: hrefForPreset(PRESET_PAGES.dashboard, f.snapshot),
        });
      });
      getSavedFilters(SAVED_FILTERS_PAGES.dashboard, user?.id).slice(0, 3).forEach((f) => {
        list.push({
          id: `sd-${f.id}`,
          label: f.name,
          hint: 'Saved filter · Dashboard',
          to: `/dashboard?view=${encodeURIComponent(f.id)}`,
        });
      });
    }
    if (canPage('reporting')) {
      getReportPresets(PRESET_PAGES.reporting, user?.id).slice(0, 4).forEach((f) => {
        list.push({
          id: `rp-r-${f.id}`,
          label: f.name,
          hint: 'Preset · Reporting',
          to: hrefForPreset(PRESET_PAGES.reporting, f.snapshot),
        });
      });
      getSavedFilters(SAVED_FILTERS_PAGES.reporting, user?.id).slice(0, 3).forEach((f) => {
        list.push({
          id: `sr-${f.id}`,
          label: f.name,
          hint: 'Saved filter · Reporting',
          to: `/reporting?view=${encodeURIComponent(f.id)}`,
        });
      });
    }
    if (canPage('roi')) {
      getReportPresets(PRESET_PAGES.roi, user?.id).slice(0, 4).forEach((f) => {
        list.push({
          id: `rp-roi-${f.id}`,
          label: f.name,
          hint: 'Preset · ROI',
          to: hrefForPreset(PRESET_PAGES.roi, f.snapshot),
        });
      });
    }
    const needle = q.trim().toLowerCase();
    if (!needle) return list.slice(0, 10);
    return list.filter((it) => `${it.label} ${it.hint}`.toLowerCase().includes(needle)).slice(0, 10);
  }, [canPage, user?.id, q]);

  if (!open) return null;

  return (
    <div className="cmdk-backdrop" onClick={() => setOpen(false)} role="presentation">
      <div
        className="cmdk-dialog"
        role="dialog"
        aria-label="Jump to"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          className="cmdk-input"
          placeholder="Jump to a page, date range, or saved view…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <ul className="cmdk-list">
          {items.map((it) => (
            <li key={it.id}>
              <button
                type="button"
                className="cmdk-item"
                onClick={() => {
                  navigate(it.to);
                  setOpen(false);
                }}
              >
                <span>{it.label}</span>
                <span className="cmdk-hint">{it.hint}</span>
              </button>
            </li>
          ))}
          {items.length === 0 && <li className="cmdk-empty">No matches</li>}
        </ul>
        <p className="cmdk-foot">Ctrl/⌘ K to toggle</p>
      </div>
    </div>
  );
}
